import React from "react";
import * as ReactDOM from "react-dom";
import { Icon, notify, confirmDialog, useLocalPref } from "../registry";
import type { SiteBundle, SiteContent } from "./types";
import { setIn, getIn, stable, normalizeContent, changedSections, previewPath, previewUrlFor, liveUrlOf, primaryHost, subdomainHost, fmtDate, fmtDateTime } from "./util";
import { WsCtx, OFFLINE_MSG } from "./fields";
import { PreviewPane } from "./preview";
import { BusinessTab, DesignTab, HomeTab, StylesTab, AreasTab, AboutTab, FinancingTab, RepairTab, CareTab, ContactTab, BlogTab, PagesTab, exampleCountOf } from "./tabs";
import { DomainTab, LeadsTab } from "./domain";
import { GrowthPanel, selfUrl } from "./growth";

/* ============================================================
   Website builder — the editor.
   • The draft lives here. Every edit is an immutable path update; 1.5 s after
     the last one the WHOLE draft goes to POST /sites/me/draft. Failed saves
     keep the edits (dirty) and offer Retry; a save never overwrites typing
     that happened while it was in flight.
   • The preview iframe remounts after each successful save.
   • Publish compares the draft with what's live (key-order-proof JSON).
   • The section list opens with the website's numbers and the day-1
     checklist (growth.tsx). "Open" on the live site carries ?__fwself=1, so
     the owner's own visits are never counted as visitors.
   ============================================================ */

/* the section list (left column): `desc` is the one-liner under each, from the draft */
const TABS: { id: string; label: string; icon: string; wide?: boolean; desc: (d: SiteContent, b: SiteBundle) => string; photos?: (d: SiteContent) => any }[] = [
  { id: "business", label: "Business", icon: "building", desc: (d) => [d.business.phone && d.business.phone.display, d.business.hoursText].filter(Boolean).join(" · ") || "Name, phone, hours, claims" },
  { id: "design", label: "Design", icon: "sliders", desc: (d) => (d.brand.logo.onDark || d.brand.logo.onLight ? "Logo" : "No logo yet") + " · colors · font" },
  { id: "home", label: "Home", icon: "dashboard", desc: (d) => d.home.h1 || "Headline, hero photo, why you", photos: (d) => d.home },
  { id: "styles", label: "Fence styles", icon: "layers", desc: (d) => { const on = d.styles.items.filter((x) => x.on); return on.length + " on · " + on.map((x) => x.name).slice(0, 3).join(", ") + (on.length > 3 ? "…" : ""); }, photos: (d) => d.styles },
  { id: "areas", label: "Service area", icon: "mappin", desc: (d) => { const n = d.areas.cities.filter((x) => x.on).length; return n + " town page" + (n === 1 ? "" : "s") + (d.areas.radiusText ? " · " + d.areas.radiusText : ""); } },
  { id: "about", label: "About", icon: "user", desc: () => "Your story, owner, numbers", photos: (d) => d.about },
  { id: "financing", label: "Financing & cost", icon: "dollar", desc: (d) => (d.financing.partnerName ? d.financing.partnerName + " · " : "") + "payments, cost guide" },
  { id: "repair", label: "Repair & gates", icon: "ruler", desc: () => "Fence repair, gates & openers", photos: (d) => d.repair },
  { id: "care", label: "Care", icon: "check", desc: () => "Care guide by material and season" },
  { id: "contact", label: "Contact & referrals", icon: "phone", desc: () => "Next steps, refer a neighbor, review page" },
  { id: "blog", label: "Blog", icon: "book", desc: (d) => (d.posts.length ? d.posts.length + " post" + (d.posts.length === 1 ? "" : "s") : "Hidden until your first post"), photos: (d) => d.posts },
  { id: "pages", label: "Pages & SEO", icon: "search", desc: (d) => { const off = Object.values(d.pages || {}).filter((m: any) => m && m.on === false).length; return "Google titles & descriptions" + (off ? ` · ${off} page${off === 1 ? "" : "s"} off` : ""); } },
  { id: "domain", label: "Domain", icon: "target", wide: true, desc: (_d, b) => primaryHost(b) },
  { id: "leads", label: "Leads & calls", icon: "send", wide: true, desc: () => "Quote requests, messages, call tracking · 30 days" },
];
const TAB_IDS = TABS.map((t) => t.id);
const SAVE_DELAY = 1500;

export function SiteEditor({ bundle, setBundle, live, store, go, params, reload, onReplayWizard }: { bundle: SiteBundle; setBundle: any; live: boolean; store: any; go: any; params: any; reload: () => Promise<any>; onReplayWizard?: () => void }) {
  const DB: any = (window as any).VFP_DB;
  const site = bundle.site!;
  const sample = !!bundle.sample;

  /* ---- the draft + autosave ---- */
  const [draft, setDraft] = React.useState<SiteContent>(() => normalizeContent(site.draft));   // a missing collection is filled in (and then autosaved)
  const draftRef = React.useRef(draft); draftRef.current = draft;
  // `_saved`: the server copy when the page hands over a locally-patched draft (the wizard's name/phone edits) — so it autosaves
  const savedRef = React.useRef<string>(stable((site as any)._saved || site.draft));
  const [saveState, setSaveState] = React.useState<"saved" | "dirty" | "saving" | "error">("saved");
  const [saveErr, setSaveErr] = React.useState("");
  const timerRef = React.useRef<any>(null);
  const inflightRef = React.useRef<Promise<boolean> | null>(null);
  const [nonce, setNonce] = React.useState(0);
  const draftStable = React.useMemo(() => stable(draft), [draft]);
  const dirty = draftStable !== savedRef.current;

  const saveNow = React.useCallback(async (): Promise<boolean> => {
    if (!live || sample) return true;
    if (inflightRef.current) { await inflightRef.current; }
    const snap = draftRef.current; const json = stable(snap);
    if (json === savedRef.current) { setSaveState("saved"); return true; }
    const p = (async () => {
      setSaveState("saving");
      try {
        const r = await DB.siteSaveDraft(snap);
        savedRef.current = json;
        setBundle((b: SiteBundle) => ({ ...b, site: { ...b.site!, ...((r && r.site) || {}), draft: snap, _saved: undefined, published: (r && r.site && "published" in r.site) ? r.site.published : b.site!.published } as any }));
        setSaveErr("");
        setSaveState(stable(draftRef.current) === json ? "saved" : "dirty");
        setNonce((n) => n + 1);
        return true;
      } catch (e: any) {
        setSaveErr(e.code === "plan" ? "Your plan no longer includes the website." : e.message || "Couldn't save");
        setSaveState("error");
        return false;
      }
    })();
    inflightRef.current = p;
    const ok = await p;
    inflightRef.current = null;
    return ok;
  }, [live, sample]);

  React.useEffect(() => {
    if (!dirty) return;
    if (sample || !live) return;
    if (saveState !== "error" && saveState !== "saving") setSaveState("dirty");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { saveNow(); }, SAVE_DELAY);
    return () => clearTimeout(timerRef.current);
  }, [draftStable]);

  /* flush before publish / AI / leaving */
  const flush = React.useCallback(async () => { clearTimeout(timerRef.current); return saveNow(); }, [saveNow]);
  React.useEffect(() => {
    const warn = (e: any) => { if (live && !sample && stable(draftRef.current) !== savedRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      // leaving the screen with unsaved edits: one last best-effort save
      if (live && !sample && stable(draftRef.current) !== savedRef.current) { try { DB.siteSaveDraft(draftRef.current).catch(() => {}); } catch (e) { /* ignore */ } }
    };
  }, []);

  const set = React.useCallback((path: any, value: any) => setDraft((d) => setIn(d, path, value)), []);
  const bind = React.useCallback((path: any) => ({ value: getIn(draftRef.current, path), onChange: (v: any) => set(path, v) }), [set]);

  /* ---- tabs, sub-page, preview ---- */
  const [tab, setTabRaw] = useLocalPref("fw_website_tab", "");   // "" = the section list
  const [sub, setSub] = React.useState<string | null>(null);
  const setTab = (t: string) => { setTabRaw(t); setSub(null); setMobileView("edit"); try { window.scrollTo(0, 0); } catch (e) { /* jsdom */ } };
  React.useEffect(() => { if (params && params.tab && TAB_IDS.includes(params.tab)) setTabRaw(params.tab, { remember: false }); }, [params && params.tab]);
  const curTab = TAB_IDS.includes(tab) ? tab : "";
  const tabDef = TABS.find((t) => t.id === curTab) || ({ id: "", wide: false } as any);
  const [device, setDevice] = useLocalPref("fw_website_device", "desktop");
  const [mobileView, setMobileView] = React.useState<"edit" | "preview">("edit");
  const path = previewPath(curTab, sub);
  const pvUrl = sample ? "" : previewUrlFor(bundle, path);

  /* ---- publish state ---- */
  const publishedN = React.useMemo(() => (site.published ? normalizeContent<SiteContent>(site.published) : null), [site.published]);
  const publishedStable = React.useMemo(() => (publishedN ? stable(publishedN) : ""), [publishedN]);
  const changes = React.useMemo(() => (publishedN ? changedSections(draft, publishedN) : []), [draftStable, publishedStable]);
  const differs = !site.published || draftStable !== publishedStable;
  const [busy, setBusy] = React.useState("");
  const host = primaryHost(bundle);
  const liveUrl = liveUrlOf(bundle);

  const publish = async () => {
    if (!live || sample) { notify(OFFLINE_MSG); return; }
    setBusy("publish");
    const ok = await flush();
    if (!ok) { setBusy(""); notify("Your latest edits haven't saved yet — fix that first (Retry), then publish.", { kind: "error" }); return; }
    const first = !site.published;
    const go2 = await confirmDialog(first
      ? { title: "Put your website live?", body: `It goes live at https://${host} in about a minute. You can keep editing — changes only go live when you publish again.`, confirmLabel: "Publish site" }
      : site.status === "paused" && !changes.length
        ? { title: "Turn your website back on?", body: `https://${host} starts showing your site again.`, confirmLabel: "Go live" }
        : { title: "Publish your changes?", body: `${changes.length ? changes.join(", ") + " — " : ""}live on https://${host} within a minute.`, confirmLabel: "Publish" });
    if (!go2) { setBusy(""); return; }
    try {
      const r = await DB.sitePublish();
      const snap = draftRef.current;
      setBundle((b: SiteBundle) => ({ ...b, site: { ...b.site!, ...((r && r.site) || {}), draft: snap, published: (r && r.site && r.site.published) || snap, status: (r && r.site && r.site.status) || "live" } }));
      notify(first ? "Your website is live." : "Published — your changes are live.");
    } catch (e: any) { notify(e.message || "Couldn't publish — try again.", { kind: "error" }); }
    setBusy("");
  };
  const unpublish = async () => {
    setMenu(false);
    if (!live || sample) { notify(OFFLINE_MSG); return; }
    if (!(await confirmDialog({ title: "Take your website offline?", body: `Visitors to https://${host} see a “site paused” page until you publish again. Your draft, domains and history stay put.`, confirmLabel: "Take offline", danger: true }))) return;
    setBusy("unpublish");
    try { const r = await DB.siteUnpublish(); setBundle((b: SiteBundle) => ({ ...b, site: { ...b.site!, ...((r && r.site) || {}), draft: draftRef.current, status: "paused" } })); notify("Your website is offline."); }
    catch (e: any) { notify(e.message || "Couldn't take it offline", { kind: "error" }); }
    setBusy("");
  };

  /* ---- AI rewrite ---- */
  const [aiBusy, setAiBusy] = React.useState("");
  const [aiModal, setAiModal] = React.useState<any>(null);
  const ai = async (req: any) => {
    if (!live || sample) { notify(OFFLINE_MSG); return; }
    setAiBusy(req.section);
    try {
      await flush();   // the service reads the saved draft (a new town / style must exist there)
      const r = await DB.siteRegenerate(req.section, req.answers);
      if (!r || r.value == null) throw new Error("The AI came back empty — try again.");
      setAiModal({ ...req, value: r.value });
    } catch (e: any) {
      notify(e.code === "credits" || e.status === 402 ? (e.message || "You're out of AI credits for this month.") : e.message || "The AI couldn't write that — try again.", { kind: "error" });
    }
    setAiBusy("");
  };

  /* ---- revisions ---- */
  const [revOpen, setRevOpen] = React.useState(false);
  const revert = async (rev: number, at: string) => {
    if (!(await confirmDialog({ title: `Go back to the version from ${fmtDate(at) || "rev " + rev}?`, body: "Your draft is replaced with that version (including anything you haven't published). It doesn't go live until you press Publish.", confirmLabel: "Restore it", danger: true }))) return false;
    try {
      clearTimeout(timerRef.current);
      const r = await DB.siteRevert(rev);
      let next = r && r.site && r.site.draft;
      if (!next) { const g = await DB.siteGet(); next = g && g.site && g.site.draft; if (g && g.site) setBundle((b: SiteBundle) => ({ ...b, ...g })); }
      if (next) { savedRef.current = stable(next); setDraft(normalizeContent(next)); setSaveState("saved"); setSaveErr(""); setNonce((n) => n + 1); }
      if (r && r.site) setBundle((b: SiteBundle) => ({ ...b, site: { ...b.site!, ...r.site } }));
      notify("Restored. Publish when you're ready to put it live.");
      return true;
    } catch (e: any) { notify(e.message || "Couldn't restore that version", { kind: "error" }); return false; }
  };

  /* ---- overflow menu ---- */
  const [menu, setMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!menu) return;
    const close = (e: any) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  const examples = React.useMemo(() => exampleCountOf(draft), [draftStable]);
  const statusChip = site.status === "live" ? <span className="chip green">Live</span> : site.status === "paused" ? <span className="chip amber">Paused</span> : <span className="chip">Draft</span>;
  const pubLabel = !site.published ? "Publish site" : site.status === "paused" && !differs ? "Go live again" : differs ? "Publish changes" : "Published";
  const canPublish = (!site.published || differs || site.status !== "live") && saveState !== "error";

  const tp = { d: draft, set, bind, ai, aiBusy, setSub, sub, store, go, live: live && !sample };
  let body: React.ReactNode = null;
  switch (curTab) {
    case "business": body = <BusinessTab {...tp} />; break;
    case "design": body = <DesignTab {...tp} />; break;
    case "styles": body = <StylesTab {...tp} />; break;
    case "areas": body = <AreasTab {...tp} />; break;
    case "about": body = <AboutTab {...tp} />; break;
    case "financing": body = <FinancingTab {...tp} />; break;
    case "repair": body = <RepairTab {...tp} />; break;
    case "care": body = <CareTab {...tp} />; break;
    case "contact": body = <ContactTab {...tp} />; break;
    case "blog": body = <BlogTab {...tp} />; break;
    case "pages": body = <PagesTab {...tp} />; break;
    case "domain": body = <DomainTab bundle={bundle} setBundle={setBundle} live={live && !sample} reload={reload} active={curTab === "domain"} sample={sample} />; break;
    case "leads": body = <LeadsTab live={live && !sample} go={go} sample={sample} bundle={bundle} />; break;
    case "home": body = <HomeTab {...tp} />; break;
    default: body = <SectionMenu d={draft} bundle={bundle} onPick={setTab} live={live} sample={sample} onPublish={publish} />;
  }

  return (
    <WsCtx.Provider value={{ live: live && !sample }}>
      <div className="ws-head">
        <div style={{ minWidth: 0 }}>
          <span className="kicker kicker-teal">Marketing</span>
          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center", margin: "6px 0 4px" }}>
            <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Website</h1>
            {statusChip}
            {sample && <span className="chip amber" title="Connect to the service to build your real site">Sample</span>}
          </div>
          <div className="ws-urlline">
            {site.status === "live" ? <a href={sample ? undefined : selfUrl(liveUrl)} target="_blank" rel="noopener" className="ws-url"><Icon name="building" size={14} />{host}</a>
              : <span className="ws-url muted"><Icon name="building" size={14} />{site.status === "paused" ? host + " · paused" : subdomainHost(bundle) + " · not published yet"}</span>}
            <SaveBadge state={sample ? "sample" : saveState} err={saveErr} onRetry={() => saveNow()} />
          </div>
        </div>
        <div className="ws-actions">
          {site.status === "live" && (sample
            ? <button type="button" className="btn btn-ghost btn-sm" onClick={() => notify("That's the sample site — yours opens here once it's live.")}><Icon name="arrow" size={14} />Open</button>
            : <a className="btn btn-ghost btn-sm" href={selfUrl(liveUrl)} target="_blank" rel="noopener"><Icon name="arrow" size={14} />Open</a>)}
          {site.status !== "live" && !sample && <a className="btn btn-ghost btn-sm" href={previewUrlFor(bundle, "/")} target="_blank" rel="noopener"><Icon name="arrow" size={14} />Open preview</a>}
          <div className="ws-pubwrap">
            <button type="button" className="btn btn-primary btn-sm" disabled={!canPublish || busy === "publish"} onClick={publish}>
              <Icon name={differs || !site.published ? "send" : "check"} size={14} />{busy === "publish" ? "Publishing…" : pubLabel}</button>
            {site.published && differs && <span className="ws-pubnote">{changes.length ? `${changes.length} section${changes.length === 1 ? "" : "s"} changed since last publish` : "Changes not published"}</span>}
          </div>
          <div className="ws-menu" ref={menuRef}>
            <button type="button" className="btn btn-ghost btn-sm ws-more" aria-label="More" aria-expanded={menu} onClick={() => setMenu(!menu)}><Icon name="menu" size={15} /></button>
            {menu && (
              <div className="ws-menu-pop" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMenu(false); setRevOpen(true); }}><Icon name="undo" size={14} />Revision history</button>
                {!sample && <a role="menuitem" href={previewUrlFor(bundle, path)} target="_blank" rel="noopener" onClick={() => setMenu(false)}><Icon name="arrow" size={14} />Open this page's preview</a>}
                {site.status === "live" && <button type="button" role="menuitem" className="danger" disabled={busy === "unpublish"} onClick={unpublish}><Icon name="lock" size={14} />Take site offline</button>}
                {sample && onReplayWizard && <button type="button" role="menuitem" onClick={() => { setMenu(false); onReplayWizard(); }}><Icon name="bolt" size={14} />Try the setup wizard</button>}
              </div>
            )}
          </div>
        </div>
      </div>

      {examples > 0 && curTab !== "domain" && curTab !== "leads" && (
        <div className="ws-banner"><Icon name="image" size={14} /><span><b>{examples} example photo{examples === 1 ? "" : "s"}</b> on your site. They're tagged “Example” until you swap in your own jobs — real work sells.</span>
          {curTab !== "styles" && <button type="button" className="rv-link" onClick={() => setTab("styles")}>Replace them</button>}</div>
      )}

      <div className="ws-mtoggle ws-seg" role="tablist" aria-label="Edit or preview">
        <button type="button" className={mobileView === "edit" ? "on" : ""} onClick={() => setMobileView("edit")}><Icon name="edit" size={13} />Edit</button>
        <button type="button" className={mobileView === "preview" ? "on" : ""} onClick={() => setMobileView("preview")} disabled={!!tabDef.wide}><Icon name="image" size={13} />Preview</button>
      </div>

      <div className={"ws-shell" + (tabDef.wide ? " wide" : "") + (mobileView === "preview" && !tabDef.wide ? " view-preview" : "")}>
        <div className="ws-edit">
          {curTab && (
            <div className="ws-sechdr">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab("")}><Icon name="chevright" size={14} style={{ transform: "rotate(180deg)" }} />All sections</button>
              <select className="select ws-input ws-jump" aria-label="Jump to section" value={curTab} onChange={(e: any) => setTab(e.target.value)}>
                {TABS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          )}
          {body}
        </div>
        {!tabDef.wide && (
          <div className="ws-pv-col">
            <PreviewPane url={pvUrl} path={path} nonce={nonce} device={device} setDevice={setDevice} sample={sample} draft={draft}
              onReload={() => setNonce((n) => n + 1)} openUrl={pvUrl} />
          </div>
        )}
      </div>

      {aiModal && <AiModal req={aiModal} onClose={() => setAiModal(null)} onApply={() => { aiModal.apply(aiModal.value); setAiModal(null); notify("Applied — it saves on its own. Publish when you're happy."); }} />}
      {revOpen && <RevisionsModal sample={sample} live={live} currentRev={site.rev} publishedAt={site.published_at} onClose={() => setRevOpen(false)} onRevert={async (rev: number, at: string) => { if (await revert(rev, at)) setRevOpen(false); }} />}
    </WsCtx.Provider>
  );
}

function SectionMenu({ d, bundle, onPick, live, sample, onPublish }: { d: SiteContent; bundle: SiteBundle; onPick: (t: string) => void; live: boolean; sample: boolean; onPublish: () => void }) {
  const pending = (bundle.domains || []).some((x) => x.status === "pending_dns" || x.status === "pending_ssl");
  const row = (t: (typeof TABS)[number]) => {
    const ex = t.photos ? exampleCountOf(t.photos(d)) : 0;
    return (
      <button key={t.id} type="button" className="ws-sec" onClick={() => onPick(t.id)}>
        <span className="ws-sec-ic"><Icon name={t.icon} size={16} /></span>
        <span className="ws-sec-txt"><b>{t.label}</b><span className="ws-row-sub">{t.desc(d, bundle)}</span></span>
        {ex > 0 && <span className="chip amber ws-sec-chip" title="Example photos to replace">{ex} example</span>}
        {t.id === "domain" && pending && <span className="chip amber ws-sec-chip">Waiting for DNS</span>}
        <Icon name="chevright" size={15} />
      </button>
    );
  };
  return (
    <>
      <GrowthPanel bundle={bundle} live={live} sample={sample} onGo={onPick} onPublish={onPublish} />
      <div className="ws-tabhead"><div><h2 className="display">Edit your site</h2><p className="muted">Pick a section. Everything saves on its own and shows in the preview — Publish puts it live.</p></div></div>
      <div className="ws-secs">{TABS.filter((t) => !t.wide).map(row)}</div>
      <div className="ws-secs-label">Site</div>
      <div className="ws-secs">{TABS.filter((t) => t.wide).map(row)}</div>
    </>
  );
}

function SaveBadge({ state, err, onRetry }: any) {
  if (state === "sample") return <span className="ws-save faint">Sample · edits stay on this screen</span>;
  if (state === "saving") return <span className="ws-save faint"><span className="ws-spin" />Saving…</span>;
  if (state === "dirty") return <span className="ws-save faint">Unsaved changes</span>;
  if (state === "error") return <span className="ws-save err"><Icon name="x" size={12} />Not saved{err ? ` — ${err}` : ""}<button type="button" className="rv-link" onClick={onRetry}>Retry</button></span>;
  return <span className="ws-save faint"><Icon name="check" size={12} />Saved</span>;
}

/* ---- AI result: old vs new for the text fields, Apply / Cancel ---- */
const FIELD_LABEL: Record<string, string> = {
  eyebrow: "Eyebrow", h1: "Headline", lead: "Intro", whyTitle: "Section label", whyHeading: "Heading", why: "Reasons", process: "Steps", servicesTitle: "Styles title", areaTitle: "Area title", areaBlurb: "Area blurb",
  story: "Story", storyTitle: "Story heading", stats: "Numbers", neverCut: "Never cut", standard: "Our standard", faqs: "FAQs", owner: "Owner",
  tagline: "Tagline", intro: "Intro", overview: "Overview", features: "Features", materials: "Materials", bestFor: "Best for", climate: "Climate", climateTitle: "Climate title", costFactors: "Cost factors", care: "Care", price: "Price line",
  why_: "Why it matters", neighborhoods: "Neighborhoods", title: "Title", excerpt: "Summary", body: "Article", category: "Category",
};
const itemText = (x: any): string => {
  if (x == null) return "";
  if (typeof x === "string") return x;
  if (x.x != null) return x.x;
  if (Array.isArray(x.items)) return x.items.map((i: string) => "• " + i).join("\n");
  if (x.q) return "Q: " + x.q + (x.a ? "\nA: " + x.a : "");
  if (x.t && x.d) return x.t + " — " + x.d;
  if (x.big) return x.big + " " + (x.small || "");
  if (x.quote) return (x.name ? x.name + ": " : "") + "“" + x.quote + "”";
  return x.t || x.title || x.name || "";
};
const asText = (v: any): string => (v == null ? "" : typeof v === "string" ? v : Array.isArray(v) ? v.map(itemText).filter(Boolean).join("\n\n") : typeof v === "object" ? itemText(v) : String(v));
function AiModal({ req, onClose, onApply }: any) {
  const v = req.value || {};
  const cur = req.current || {};
  const rows = Object.keys(v).filter((k) => !["slug", "on", "thumb", "heroPhoto", "gallery", "photo", "image", "dateISO", "date", "read", "popular"].includes(k))
    .map((k) => ({ k, label: FIELD_LABEL[k === "why" && req.section.startsWith("city:") ? "why_" : k] || k, old: asText(cur[k]), nu: asText(v[k]) }))
    .filter((r) => r.nu && r.nu !== r.old);
  const isNew = !req.current;
  return ReactDOM.createPortal(
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal modal-lg ws-ai" onClick={(e: any) => e.stopPropagation()}>
        <div className="row between" style={{ gap: 10, flexWrap: "wrap" }}>
          <div>
            <span className="kicker kicker-teal">AI {isNew ? "draft" : "rewrite"}</span>
            <h3 className="display" style={{ fontSize: 19, margin: "4px 0 2px" }}>{req.label}</h3>
            <p className="faint" style={{ fontSize: 12, margin: 0 }}>Used 1 AI credit. Nothing changes until you press Apply — then edit anything you like.</p>
          </div>
        </div>
        <div className="ws-diff">
          {!rows.length && <p className="muted">The AI came back with the same text. Try again, or add a few facts under Business → Claims.</p>}
          {rows.map((r) => (
            <div key={r.k} className="ws-diff-row">
              <div className="ws-diff-k">{r.label}</div>
              <div className={"ws-diff-cols" + (isNew || !r.old ? " one" : "")}>
                {!isNew && r.old && <div className="ws-diff-old"><span className="faint">Now</span><p>{r.old}</p></div>}
                <div className="ws-diff-new"><span>{isNew || !r.old ? "Draft" : "Rewrite"}</span><p>{r.nu}</p></div>
              </div>
            </div>
          ))}
        </div>
        <div className="row gap-8" style={{ justifyContent: "flex-end", marginTop: 16 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!rows.length} onClick={onApply}><Icon name="check" size={15} />{isNew ? "Add it" : "Apply"}</button>
        </div>
      </div>
    </div>, document.body);
}

function RevisionsModal({ sample, live, currentRev, publishedAt, onClose, onRevert }: any) {
  const DB: any = (window as any).VFP_DB;
  const [revs, setRevs] = React.useState<any[] | null>(null);
  const [err, setErr] = React.useState("");
  const [busy, setBusy] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (sample || !live) {
      const DAY = 86400000;
      setRevs([{ rev: 3, created_at: publishedAt || new Date(Date.now() - 3 * DAY).toISOString() }, { rev: 2, created_at: new Date(Date.now() - 12 * DAY).toISOString() }, { rev: 1, created_at: new Date(Date.now() - 30 * DAY).toISOString() }]);
      return;
    }
    DB.siteRevisions().then((r: any) => setRevs((r && r.revisions) || [])).catch((e: any) => { setErr(e.message || "Couldn't load history"); setRevs([]); });
  }, []);
  const list = (revs || []).slice().sort((a, b) => b.rev - a.rev);
  return ReactDOM.createPortal(
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ width: "min(520px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
        <h3 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>Revision history</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 14px" }}>Every publish is saved (the last 20). Restoring one puts it back in your draft — publish to make it live again.</p>
        {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}
        {!revs ? <p className="muted">Loading…</p> : !list.length ? <p className="muted" style={{ fontSize: 13.5 }}>Nothing published yet. Each time you publish, a copy is kept here.</p> : (
          <div className="ws-revs">
            {list.map((r) => (
              <div key={r.rev} className="ws-rev">
                <div style={{ minWidth: 0 }}><b>Version {r.rev}</b>{r.rev === currentRev && <span className="chip green" style={{ marginLeft: 8, fontSize: 10.5, padding: "1px 7px" }}>Live now</span>}
                  <div className="faint" style={{ fontSize: 12 }}>Published {fmtDateTime(r.created_at)}</div></div>
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy != null} onClick={async () => {
                  if (sample || !live) { notify(OFFLINE_MSG); return; }
                  setBusy(r.rev); await onRevert(r.rev, r.created_at); setBusy(null);
                }}><Icon name="undo" size={13} />{busy === r.rev ? "Restoring…" : "Restore"}</button>
              </div>
            ))}
          </div>
        )}
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 16 }}><button type="button" className="btn btn-ghost" onClick={onClose}>Close</button></div>
      </div>
    </div>, document.body);
}
